/*
 * cx-count-up.js — opt-in "count up when it scrolls into view" for stat numbers.
 *
 * Deliberately built to match assets/calypso-motion.css: everything is opt-in,
 * nothing animates unless an element asks for it, and the finished value is
 * what sits in the HTML. If this script never runs -- no JS, an error upstream,
 * an old browser -- the real number is already on the page. Nothing to fall
 * back to, because the correct state IS the default state.
 *
 * Usage:
 *   <span data-count-up data-count-duration="1600">3,700+</span>
 *
 * Attributes:
 *   data-count-up        opts the element in (required)
 *   data-count-duration  milliseconds, default 1600
 *
 * Adds `.cx-count-up--running` to the element while it animates, so a section
 * can style the in-flight state (e.g. tabular figures) without changing how the
 * number looks at rest.
 *
 * WHAT IT WILL AND WON'T ANIMATE
 * ------------------------------
 * The value is free text a merchant types in the customizer, so it is not safe
 * to assume it is a number. The digits must START the string, after at most a
 * two-character symbol prefix, with at most a four-character suffix:
 *
 *   "3,700+"        -> counts to 3,700 and keeps the "+"
 *   "500+"          -> counts to 500
 *   "2013"          -> counts to 2013
 *   "$1.2M"         -> counts to 1.2, keeps "$" and "M", keeps one decimal
 *   "Every 2 weeks" -> LEFT ALONE. Prose that happens to contain a digit would
 *                      look broken counting "Every 0 weeks" upward.
 *   "500 designs"   -> left alone (suffix too long to be a unit)
 *
 * Grouping and decimal places are copied from what the merchant typed, so
 * "3,700+" counts with a comma and "1.2" keeps its single decimal. On the last
 * frame the original string is restored verbatim rather than reformatted, so
 * the resting value is always exactly what was authored.
 *
 * Accessibility: respects prefers-reduced-motion by leaving the final value in
 * place. The animating element is aria-hidden by the markup that uses it, with
 * a visually-hidden copy carrying the real value for screen readers, so the
 * number is never mid-count for assistive tech.
 */
(function () {
  const SELECTOR = '[data-count-up]';
  const RUNNING_CLASS = 'cx-count-up--running';
  const DEFAULT_DURATION = 1600;

  /* Digits must lead. The tight prefix/suffix budgets are what keep prose out. */
  const VALUE_PATTERN = /^([^\d]{0,2})(\d[\d,]*(?:\.\d+)?)([^\d]{0,4})$/;

  let observer = null;

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function parseValue(text) {
    const match = VALUE_PATTERN.exec(String(text).trim());

    if (!match) return null;

    const digits = match[2];
    const value = parseFloat(digits.replace(/,/g, ''));

    if (!isFinite(value)) return null;

    const dot = digits.indexOf('.');

    return {
      prefix: match[1],
      suffix: match[3],
      value: value,
      decimals: dot === -1 ? 0 : digits.length - dot - 1,
      grouped: digits.indexOf(',') !== -1
    };
  }

  function format(number, spec) {
    const fixed = number.toFixed(spec.decimals);

    if (!spec.grouped) return spec.prefix + fixed + spec.suffix;

    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return spec.prefix + parts.join('.') + spec.suffix;
  }

  /* ease-out cubic: quick off the mark, settles gently onto the real number */
  function ease(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animate(element, spec, duration, finalText) {
    /* Reserve the width the finished number needs before we shrink it to zero,
       otherwise the whole centred stat row reflows on every frame. Measured
       while the element still holds its final text, released at the end so the
       resting layout is untouched. */
    const width = element.getBoundingClientRect().width;

    if (width) element.style.minWidth = width + 'px';

    element.classList.add(RUNNING_CLASS);

    let startedAt = null;

    function frame(now) {
      if (startedAt === null) startedAt = now;

      const progress = Math.min((now - startedAt) / duration, 1);

      if (progress < 1) {
        element.textContent = format(spec.value * ease(progress), spec);
        window.requestAnimationFrame(frame);
        return;
      }

      element.textContent = finalText;
      element.classList.remove(RUNNING_CLASS);
      element.style.minWidth = '';
    }

    window.requestAnimationFrame(frame);
  }

  function activate(element) {
    if (element.dataset.countUpDone === 'true') return;

    element.dataset.countUpDone = 'true';

    const finalText = element.textContent;
    const spec = parseValue(finalText);

    /* Not a number we can count, or the visitor asked for less motion. Either
       way the finished value is already rendered, so there is nothing to do. */
    if (!spec || prefersReducedMotion()) return;

    const duration = parseInt(element.dataset.countDuration, 10) || DEFAULT_DURATION;

    animate(element, spec, duration, finalText);
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          observer.unobserve(entry.target);
          activate(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    return observer;
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const active = getObserver();

    /* No IntersectionObserver: leave every value at its authored state. */
    if (!active) return;

    scope.querySelectorAll(SELECTOR).forEach(function (element) {
      if (element.dataset.countUpBound === 'true') return;

      element.dataset.countUpBound = 'true';
      active.observe(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor re-renders a section on every settings change. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
